import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { applyOptionalClientTextFields, normalizeClientPhoneForDedup } from './clientMapper';
import type { ClientImportPersistRow } from '../features/super-admin/utils/clientImportExport';

export type ClientBulkImportAuth = {
  isSuperAdmin: boolean;
  isCSM: boolean;
  userBusinessIds: string[] | undefined;
};

export type ClientBulkImportRowError = {
  rowIndex: number;
  name: string;
  message: string;
};

export type ClientBulkImportProgressEvent =
  | { kind: 'start'; total: number }
  | { kind: 'row'; index: number; total: number; name: string }
  | { kind: 'success'; index: number; total: number; name: string; clientId: string }
  | { kind: 'fail'; index: number; total: number; name: string; message: string }
  | { kind: 'done'; created: number; errors: ClientBulkImportRowError[] };

export type ClientBulkImportResult = {
  created: number;
  ids: string[];
  errors: ClientBulkImportRowError[];
};

/** Accès import bulk : Super Admin, ou CSM rattaché au business cible. */
export function assertBulkImportBusinessAccess(
  auth: ClientBulkImportAuth,
  targetBusinessId: string
): void {
  const { isSuperAdmin, isCSM, userBusinessIds } = auth;
  if (!isSuperAdmin && !(isCSM && (userBusinessIds?.includes(targetBusinessId) ?? false))) {
    throw new Error('Accès refusé pour ce business.');
  }
}

export async function loadExistingClientPhonesNormalized(businessId: string): Promise<Set<string>> {
  const snap = await getDocs(
    query(collection(db, COLLECTIONS.clients), where('businessId', '==', businessId))
  ).catch((error) => {
    handleFirestoreError(error, 'loadExistingClientPhonesNormalized');
    throw error;
  });

  const phones = new Set<string>();
  for (const docSnap of snap.docs) {
    const phone = String(docSnap.data().phone ?? '').trim();
    if (!phone) continue;
    const normalized = normalizeClientPhoneForDedup(phone);
    if (normalized) phones.add(normalized);
  }
  return phones;
}

export async function runSuperAdminBulkImportClients(params: {
  businessId: string;
  rows: ClientImportPersistRow[];
  auth: ClientBulkImportAuth;
  onProgress?: (e: ClientBulkImportProgressEvent) => void;
}): Promise<ClientBulkImportResult> {
  const { businessId, rows, auth, onProgress } = params;
  assertBulkImportBusinessAccess(auth, businessId);

  const usedPhones = await loadExistingClientPhonesNormalized(businessId);
  const createdIds: string[] = [];
  const errors: ClientBulkImportRowError[] = [];

  onProgress?.({ kind: 'start', total: rows.length });

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const displayName = row.name.trim() || `Ligne ${i + 1}`;
    onProgress?.({ kind: 'row', index: i, total: rows.length, name: displayName });

    try {
      const phoneNorm = row.phone ? normalizeClientPhoneForDedup(row.phone) : '';
      if (phoneNorm) {
        if (usedPhones.has(phoneNorm)) {
          throw new Error('Ce numéro existe déjà dans la base clients du business.');
        }
        usedPhones.add(phoneNorm);
      }

      const docData: Record<string, unknown> = {
        name: row.name.trim(),
        businessId,
        loyaltyBalance: 0,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      applyOptionalClientTextFields(docData, row);

      if (row.birthDate) {
        docData.birthDate = Timestamp.fromDate(row.birthDate);
      }

      if (row.marketingOptIn !== undefined) {
        docData.preferences = { marketingOptIn: row.marketingOptIn };
      }

      const docRef = await addDoc(collection(db, COLLECTIONS.clients), docData);
      createdIds.push(docRef.id);

      onProgress?.({
        kind: 'success',
        index: i,
        total: rows.length,
        name: displayName,
        clientId: docRef.id,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      errors.push({ rowIndex: i, name: displayName, message });
      onProgress?.({
        kind: 'fail',
        index: i,
        total: rows.length,
        name: displayName,
        message,
      });
    }
  }

  onProgress?.({ kind: 'done', created: createdIds.length, errors });
  return { created: createdIds.length, ids: createdIds, errors };
}
